import { nativeCall, type NativeCallState } from './nativeBridge';

/** Metadata updates do not replace the authorized peer. State/pause transitions do. */
export const sameNativeCallScope = (a: NativeCallState, b: NativeCallState) =>
  a.meetingId === b.meetingId && a.generation === b.generation && a.state === b.state &&
  Boolean(a.accessVerifying) === Boolean(b.accessVerifying);

/** In-memory acknowledgements only: no receipt can authorize a different native peer. */
export type NativeViewportReceipt = { meetingId: string; generation: number; revision: number };
let revision = 0;
const acknowledged = new Set<(receipt: NativeViewportReceipt) => void>();
const refresh = new Set<() => void>();
export const nextViewportRevision = () => ++revision;
export const currentViewportRevision = () => revision;
export const acknowledgeNativeViewport = (receipt: NativeViewportReceipt) => {
  if (receipt.revision === revision) acknowledged.forEach(listener => listener(receipt));
};
export const onNativeViewportAcknowledged = (listener: (receipt: NativeViewportReceipt) => void) => {
  acknowledged.add(listener);
  return () => { acknowledged.delete(listener); };
};
export const onNativeViewportRefresh = (listener: () => void) => {
  refresh.add(listener);
  return () => { refresh.delete(listener); };
};
export const refreshNativeViewport = () => { refresh.forEach(listener => listener()); };

/** True when a viewport rejection only means native already moved to another call scope. */
export async function viewportScopeRetired(error: unknown, meetingId: string, generation: number) {
  if (typeof error !== 'object' || error === null || !('code' in error) || error.code !== 'STALE_CALL') return false;
  try {
    // Native room changes can retire this scope before React receives
    // its next snapshot. STALE_CALL alone can also mean a missing view.
    const current = await nativeCall.getState();
    return Boolean(current && typeof current.meetingId === 'string' && current.meetingId.trim().length > 0
      && typeof current.generation === 'number' && Number.isSafeInteger(current.generation) && current.generation >= 0
      && ['idle', 'preview', 'joining', 'joined', 'waiting', 'denied', 'left', 'error'].includes(current.state)
      && (current.meetingId !== meetingId || current.generation !== generation));
  } catch {
    // An unreadable scope does not establish that this failure is stale.
    return false;
  }
}
